import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { PlatformProvider } from "@/context/PlatformContext";
import { PreviewProvider } from "@/context/PreviewContext";
import Login from "@/pages/Login";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvitation from "@/pages/AcceptInvitation";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Leads from "@/pages/Leads";
import Products from "@/pages/Products";
import Orders from "@/pages/Orders";
import OrderDetails from "@/pages/OrderDetails";
import Quotations from "@/pages/Quotations";
import Payments from "@/pages/Payments";
import Invoices from "@/pages/Invoices";
import Tasks from "@/pages/Tasks";
import FollowUps from "@/pages/FollowUps";
import CalendarPage from "@/pages/CalendarPage";
import Reports from "@/pages/Reports";
import AIAssistant from "@/pages/AIAssistant";
import Employees from "@/pages/Employees";
import Incentives from "@/pages/Incentives";
import Settings from "@/pages/Settings";
import Integrations from "@/pages/Integrations";
import PlatformAdmin from "@/pages/PlatformAdmin";
import AuditLog from "@/pages/AuditLog";
import ModuleRunner from "@/pages/ModuleRunner";
import FormRunner from "@/pages/FormRunner";
import "@/App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <ThemeProvider>
      <PlatformProvider>
        <PreviewProvider>
        <AuthProvider>
          <BrowserRouter>
          <Toaster richColors position="top-right" />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/" element={<Protected><Layout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="customers" element={<Customers />} />
              <Route path="customers/:id" element={<CustomerDetail />} />
              <Route path="leads" element={<Leads />} />
              <Route path="products" element={<Products />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetails />} />
              <Route path="quotations" element={<Quotations />} />
              <Route path="payments" element={<Payments />} />
              <Route path="invoices" element={<Invoices />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="followups" element={<FollowUps />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="reports" element={<Reports />} />
              <Route path="ai" element={<AIAssistant />} />
              <Route path="employees" element={<Employees />} />
              <Route path="incentives" element={<Incentives />} />
              <Route path="settings" element={<Settings />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="platform" element={<PlatformAdmin />} />
              <Route path="m/:slug" element={<ModuleRunner />} />
              <Route path="f/:slug" element={<FormRunner />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </PreviewProvider>
      </PlatformProvider>
    </ThemeProvider>
  );
}
